import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Package, AlertTriangle, Clock, BarChart3, ArrowLeft,
  ShieldAlert, Loader2, Activity, CheckCircle, XCircle, ArrowUpRight,
  Boxes, Thermometer, ChevronDown, ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (v) => new Intl.NumberFormat("fr-FR").format(Math.round(v || 0));

const URGENCY_CONFIG = {
  HIGH: { label: "Critique", color: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
  MEDIUM: { label: "Bas", color: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  LOW: { label: "Surstock", color: "bg-blue-100 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  NONE: { label: "Optimal", color: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
};

const ALERT_CONFIG = {
  CRITICAL: { color: "border-red-200 bg-red-50", text: "text-red-700", badge: "bg-red-100 text-red-700" },
  HIGH: { color: "border-amber-200 bg-amber-50", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  MEDIUM: { color: "border-sky-200 bg-sky-50", text: "text-sky-700", badge: "bg-sky-100 text-sky-700" },
};

const PIE_COLORS = ["#ef4444", "#f59e0b", "#3b82f6", "#10b981"];

export default function StockForecastPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("forecasts");
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stock-forecast/analysis`, { headers: getHeaders() });
      setData(res.data);
    } catch (err) {
      toast.error("Erreur de chargement des previsions de stock");
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
        <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const forecasts = data?.forecasts || [];
  const alerts = data?.alerts || [];

  // Chart data
  const stockChart = forecasts.slice(0, 8).map(f => ({
    name: f.category.charAt(0).toUpperCase() + f.category.slice(1),
    stock: f.current_stock,
    reorder: f.reorder_point,
  }));

  const riskPie = [
    { name: "Expires", value: summary.expired_products || 0 },
    { name: "< 3 mois", value: summary.expiring_3m || 0 },
    { name: "En stock", value: Math.max(0, (summary.total_products || 0) - (summary.expired_products || 0) - (summary.expiring_3m || 0)) },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdf4 100%)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-slate-600" data-testid="forecast-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Previsions de Stock IA
            </h1>
            <p className="text-sm text-slate-500">Analyse predictive basee sur vos donnees historiques</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="forecast-kpis">
          {[
            { icon: Package, label: "Produits", value: summary.total_products, color: "sky" },
            { icon: BarChart3, label: "Valeur stock", value: `${fmt(summary.total_stock_value)} F`, color: "emerald" },
            { icon: AlertTriangle, label: "Valeur a risque", value: `${fmt(summary.value_at_risk)} F`, color: "red" },
            { icon: ShieldAlert, label: "Categories critiques", value: summary.critical_categories, color: "amber" },
            { icon: Thermometer, label: "Score risque", value: `${summary.expiry_risk_score}/100`, color: summary.expiry_risk_score > 50 ? "red" : "sky" },
          ].map((kpi) => (
            <Card key={kpi.label} className="bg-white border-slate-200" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <kpi.icon className={`w-4 h-4 text-${kpi.color}-600`} />
                  <span className="text-xs text-slate-500 font-medium">{kpi.label}</span>
                </div>
                <p className="text-lg font-bold text-slate-900">{kpi.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Stock vs Reorder */}
          <Card className="bg-white border-slate-200" data-testid="stock-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-900">Stock actuel vs Seuil de reapprovisionnement</CardTitle>
            </CardHeader>
            <CardContent>
              {stockChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stockChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="stock" fill="#0284c7" name="Stock actuel" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="reorder" fill="#f59e0b" name="Seuil commande" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-60 flex items-center justify-center text-slate-400 text-sm">Aucune donnee disponible</div>
              )}
            </CardContent>
          </Card>

          {/* Risk Pie */}
          <Card className="bg-white border-slate-200" data-testid="risk-pie-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-900">Repartition des risques de peremption</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-center">
              {riskPie.length > 0 ? (
                <div className="flex items-center gap-8">
                  <ResponsiveContainer width={200} height={200}>
                    <PieChart>
                      <Pie data={riskPie} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                        {riskPie.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3">
                    {riskPie.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-sm text-slate-600">{d.name}: <strong className="text-slate-900">{d.value}</strong></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">Aucun produit</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: "forecasts", label: "Previsions par categorie", count: forecasts.length },
            { id: "alerts", label: "Alertes produits", count: alerts.length },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? "bg-sky-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}
              data-testid={`tab-${t.id}`}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* Forecasts Table */}
        {tab === "forecasts" && (
          <div className="space-y-3" data-testid="forecasts-list">
            {forecasts.length === 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-8 text-center text-slate-500">
                  <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>Aucune donnee de stock trouvee. Ajoutez des produits pour generer des previsions.</p>
                </CardContent>
              </Card>
            ) : (
              forecasts.map((f, i) => {
                const urg = URGENCY_CONFIG[f.urgency] || URGENCY_CONFIG.NONE;
                const isExpanded = expanded === i;
                return (
                  <Card key={f.category} className="bg-white border-slate-200 overflow-hidden" data-testid={`forecast-${f.category}`}>
                    <button onClick={() => setExpanded(isExpanded ? null : i)} className="w-full text-left">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-2.5 h-2.5 rounded-full ${urg.dot}`} />
                            <div>
                              <p className="text-base font-semibold text-slate-900 capitalize">{f.category}</p>
                              <p className="text-xs text-slate-500">{f.total_products} produits | {fmt(f.stock_value)} FCFA</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${urg.color}`}>
                              {urg.label}
                            </span>
                            <div className="text-right hidden sm:block">
                              <p className="text-sm font-bold text-slate-900">{f.current_stock} unites</p>
                              <p className="text-xs text-slate-500">{f.months_remaining < 99 ? `~${f.months_remaining} mois restants` : "Stock durable"}</p>
                            </div>
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                        </div>
                      </CardContent>
                    </button>
                    {isExpanded && (
                      <div className="border-t border-slate-100 px-5 pb-5 pt-4 bg-slate-50/50">
                        <div className="grid sm:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Consommation mensuelle</p>
                            <p className="text-sm font-bold text-slate-900">{f.monthly_consumption} unites/mois</p>
                            {f.seasonal_factor !== 1.0 && (
                              <p className="text-xs text-amber-600 mt-0.5">Ajuste: {f.adjusted_consumption} (saison x{f.seasonal_factor})</p>
                            )}
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Seuil de reapprovisionnement</p>
                            <p className="text-sm font-bold text-slate-900">{f.reorder_point} unites</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Taux de perte</p>
                            <p className="text-sm font-bold text-slate-900">{f.waste_rate}%</p>
                            <p className="text-xs text-slate-400">{f.destructions.qty_destroyed} unites detruites</p>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Perimes</p>
                            <p className="text-sm font-bold text-red-600">{f.expired_count}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Expirent sous 3 mois</p>
                            <p className="text-sm font-bold text-amber-600">{f.expiring_3m}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 mb-1">Risque peremption</p>
                            <p className="text-sm font-bold text-slate-900">{f.expiry_risk}/100</p>
                          </div>
                        </div>
                        <div className="bg-white rounded-lg border border-slate-200 p-3">
                          <p className="text-xs font-semibold text-sky-600 mb-1">Recommandation IA</p>
                          <p className="text-sm text-slate-700">{f.recommendation}</p>
                        </div>
                      </div>
                    )}
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Alerts Table */}
        {tab === "alerts" && (
          <div className="space-y-2" data-testid="alerts-list">
            {alerts.length === 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-8 text-center text-slate-500">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
                  <p>Aucune alerte de stock active. Tous vos produits sont en bon etat.</p>
                </CardContent>
              </Card>
            ) : (
              alerts.map((a, i) => {
                const cfg = ALERT_CONFIG[a.severity] || ALERT_CONFIG.MEDIUM;
                return (
                  <div key={a.product_id + '-' + (a.batch || i)} className={`rounded-xl border p-4 ${cfg.color}`} data-testid={`alert-${a.product_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.badge}`}>
                            {a.alert_type === "EXPIRE" ? "Expire" : a.alert_type === "EXPIRING_SOON" ? "Urgent" : "A surveiller"}
                          </span>
                          <span className="text-xs text-slate-500">{a.batch}</span>
                        </div>
                        <p className={`text-sm font-semibold ${cfg.text}`}>{a.name}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          {a.quantity} unites | {fmt(a.value_at_risk)} FCFA | Exp: {a.expiry_date}
                          {a.days_until_expiry !== undefined && ` (${a.days_until_expiry}j)`}
                        </p>
                      </div>
                      <p className="text-xs text-slate-600 max-w-xs text-right">{a.action}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
